import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, timer } from 'rxjs';
import { catchError, switchMap, tap, filter, take, retryWhen, delay, mergeMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '@src/environments/environment';
import { SessionHelper } from './sessionHelper';

interface QueuedRequest {
  request: HttpRequest<any>;
  next: HttpHandler;
  subject: BehaviorSubject<HttpEvent<any>>;
}

@Injectable()
export class AddtokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private requestQueue: QueuedRequest[] = [];
  private networkErrorCount = 0;
  private readonly MAX_NETWORK_ERRORS = 2;
  private readonly REFRESH_MAX_RETRIES = 3;

  private readonly AUTH_BYPASS_PATHS = [
    '/api/auth/login',
    '/login',
    '/api/tokenlogin',
    '/tokenlogin',
    '/api/auth/logout',
    '/logout',
  ];

  constructor(private http: HttpClient, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip interceptor for specific URLs that don't need authentication
    // Presigned S3/MinIO URLs are self-authenticating via URL parameters
    if (req.url.includes("worktrest-docstore") || req.url.includes("logostore") || 
        req.url.includes("forgot") || req.url.includes("reset") ||
        req.url.includes("X-Amz-Signature") || req.url.startsWith(environment.logostore)) {
      return next.handle(req);
    }

    // Skip interceptor for OPTIONS requests (CORS preflight)
    if (req.method === 'OPTIONS') {
      return next.handle(req);
    }

    // Skip interceptor for refresh endpoint to avoid infinite loops
    if (req.url.includes("/refresh")) {
      return next.handle(req.clone({ withCredentials: true }));
    }

    // Skip auth handling for login/token endpoints to avoid redirect loops
    if (this.shouldBypassAuthHandling(req.url)) {
      return next.handle(req.clone({ withCredentials: true }));
    }

    return this.handleRequestWithAuth(req, next);
  }

  private handleRequestWithAuth(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const requestWithCredentials = req.clone({ withCredentials: true });
    
    return next.handle(requestWithCredentials).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(`⚠️ HTTP Error: ${error.status} - ${error.message} for ${req.method} ${req.url}`, error);
        
        // Handle 401 Unauthorized - token expired or invalid
        if (error.status === 401 && SessionHelper.getUserSub() && SessionHelper.getUserName()) {
          console.warn(`🔄 Token expired for ${req.url}, attempting refresh...`);
          return this.handle401Error(req, next);
        }
        
        // Handle 403 Forbidden - insufficient permissions
        if (error.status === 403) {
          console.error("❌ Forbidden: Access denied");
          return throwError(() => new Error("Access denied: Insufficient permissions"));
        }
        
        // Handle network/CORS errors (status 0, 500+, etc.)
        if (this.isNetworkError(error)) {
          console.error(`🌐 Network/CORS error for ${req.url}:`, error);
          
          // If user is logged in but getting CORS errors, it's likely token expiration
          // causing the authorizer to reject requests before they reach CORS handling
          if (SessionHelper.getUserSub() && SessionHelper.getUserName()) {
            console.warn(`🔄 CORS error for authenticated user - treating as token expiration`);
            return this.handle401Error(req, next);
          }
          
          return this.handleNetworkError(error);
        }
        
        // For other errors, pass them through
        return throwError(() => new Error(error.message || 'API request failed'));
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject.next(null);
      
      console.warn('🔄 Access token expired. Refreshing...');
     //console.log('🍪 Current cookies:', document.cookie);
     //console.log('🔗 Request URL:', req.url);
     //console.log('🌐 Environment API URL:', environment.apiUrl);
      
      return this.performTokenRefresh().pipe(
        switchMap(() => {
          console.info('✅ Token refresh successful');
          this.isRefreshing = false;
          this.refreshSubject.next(true);
          this.processRequestQueue();
          return next.handle(req.clone({ withCredentials: true }));
        }),
        catchError(refreshError => {
          console.error('❌ Token refresh failed. Error details:', refreshError);
          console.error('❌ Error status:', refreshError?.status);
          console.error('❌ Error message:', refreshError?.message);
          
          // Check if this is a CORS/network error
          if (this.isNetworkError(refreshError)) {
            console.error('❌ Refresh failed due to CORS/network error. Check backend CORS configuration.');
          }
          
          this.isRefreshing = false;
          this.refreshSubject.next(false);
          this.clearRequestQueue();
          this.redirectToLogin();
          return throwError(() => new Error("Token refresh failed: Redirecting to login"));
        })
      );
    } else {
      // Token refresh is in progress, queue this request
      console.info("🔁 Queuing request during token refresh");
      return this.addToQueue(req, next);
    }
  }

  private performTokenRefresh(): Observable<any> {
    const refreshRequest = {
      username: SessionHelper.getUserName()
    };

   //console.log(`🔄 Attempting token refresh for user: ${SessionHelper.getUserName()} at ${environment.apiUrl}/refresh`);
    
    return this.http.post(`${environment.apiUrl}/refresh`, refreshRequest, { 
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    }).pipe(
      retryWhen(errors => 
        errors.pipe(
          mergeMap((error, index) => {
            console.error(`🔄 Refresh attempt ${index + 1} failed:`, error);
            
            if (index >= this.REFRESH_MAX_RETRIES - 1) {
              console.error(`❌ Refresh failed after ${this.REFRESH_MAX_RETRIES} attempts. Final error:`, error);
              return throwError(() => error);
            }
            
            const delayTime = Math.min(1000 * Math.pow(2, index), 5000); // Exponential backoff, max 5s
            console.warn(`🔄 Retrying refresh in ${delayTime}ms... (attempt ${index + 2}/${this.REFRESH_MAX_RETRIES})`);
            return timer(delayTime);
          })
        )
      ),
      tap(() => {
        // Reset network error count on successful refresh
        this.networkErrorCount = 0;
      })
    );
  }

  private addToQueue(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const subject = new BehaviorSubject<HttpEvent<any>>(null as any);
    
    this.requestQueue.push({
      request: req,
      next: next,
      subject: subject
    });

    // Wait for refresh to complete
    return this.refreshSubject.pipe(
      filter(result => result !== null),
      take(1),
      switchMap(success => {
        if (success) {
          // Remove this request from queue and execute it
          const queuedItem = this.requestQueue.find(item => item.subject === subject);
          if (queuedItem) {
            this.requestQueue = this.requestQueue.filter(item => item !== queuedItem);
            return next.handle(req.clone({ withCredentials: true }));
          }
        }
        return throwError(() => new Error("Token refresh failed"));
      })
    );
  }

  private processRequestQueue(): void {
    console.info(`🔄 Processing ${this.requestQueue.length} queued requests`);
    
    this.requestQueue.forEach(queuedItem => {
      queuedItem.next.handle(queuedItem.request.clone({ withCredentials: true })).subscribe({
        next: event => queuedItem.subject.next(event),
        error: error => queuedItem.subject.error(error),
        complete: () => queuedItem.subject.complete()
      });
    });
    
    this.requestQueue = [];
  }

  private clearRequestQueue(): void {
    console.info(`❌ Clearing ${this.requestQueue.length} queued requests`);
    
    this.requestQueue.forEach(queuedItem => {
      queuedItem.subject.error(new Error("Authentication failed"));
    });
    
    this.requestQueue = [];
  }

  private isNetworkError(error: HttpErrorResponse): boolean {
    // Network errors typically have status 0 or are in 500+ range
    // CORS errors also typically show as status 0
    return error.status === 0 || error.status >= 500;
  }

  private handleNetworkError(error: HttpErrorResponse): Observable<never> {
    this.networkErrorCount++;
    
    console.error(`❌ Network error (${this.networkErrorCount}/${this.MAX_NETWORK_ERRORS}):`, error);
    
    if (this.networkErrorCount >= this.MAX_NETWORK_ERRORS) {
      console.error('❌ Too many network errors. Keeping user on current screen and surfacing API/CORS failure.');
      this.networkErrorCount = 0; // Reset counter
      return throwError(() => new Error('Network/CORS failure: backend not reachable or credentials blocked'));
    }
    
    // For first network error, just pass it through
    return throwError(() => new Error(error.message || 'Network error occurred'));
  }

  private shouldBypassAuthHandling(url: string): boolean {
    return this.AUTH_BYPASS_PATHS.some((path) => url.includes(path));
  }

  private redirectToLogin(): void {
    SessionHelper.clear();
    this.router.navigate(['/login']);
  }
}
